/**
 * LightingModule — physically-based sun + shadow (Graphics G3, Phase B).
 *
 * After the Phase B sky swap, this module no longer owns IBL / sky-colour /
 * envmap loading.  Those responsibilities now belong to:
 *   - `SkyModule` (atlas) → wraps the EngineX Hillaire SkyModule, which bakes
 *     a PMREM IBL cube from the procedural sky and assigns `scene.environment`.
 *
 * What this module DOES own:
 *   - Sun `DirectionalLight` — arc driven by `sunPosition(tod)`, color/intensity
 *     lerped from the `SkyLightingProfile` band values.
 *   - `HemisphereLight` ambient — intensity lerped from the same profiles.
 *   - ACES tone mapping + exposure.
 *   - CSM sun shadows (WebGPU node CSM) — shadow-cost bounded by per-shadow
 *     `autoUpdate` gating (re-renders only when the sun moves or casters change).
 *   - Band profile subscription (still needed for sun color/intensity/ambient).
 *
 * IBL (scene.environment) is provided by the engine's PMREM bake in SkyModule.
 * This module no longer calls `assets.loadEnvMap`, sets `scene.environment`,
 * or sets `scene.background`.
 *
 * The `assets` constructor parameter is retained (it is still referenced by
 * several render verify harnesses that construct `new LightingModule(assets)`)
 * even though this module no longer calls `assets.loadEnvMap`.  Removing it
 * would require touching g3-lighting.ts, g3-shadows.ts, and g5-tiers.ts, which
 * are Playwright-driven harnesses not in the typecheck/build gate scope of this
 * task.  The parameter is intentionally kept as a no-op; a future cleanup sweep
 * can remove it once those harnesses are updated.
 */
import * as THREE from 'three/webgpu';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { worldEnv, TINT_BLEND } from '../lib/world_env.js';
import { bus } from '../bus.js';
import type { AssetModule } from './AssetModule.js';
import { type Band, bandForTimeOfDay, sunPosition, lerpColorHexInto } from '../lib/lighting.js';
import { activePreset } from '../lib/quality_tiers.js';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { currentTod } from './SkyModule.js';

interface Profile { envmapAssetId: bigint; sunColorHex: string; sunIntensity: number; ambientIntensity: number; }

export class LightingModule implements IModule {
  readonly name = 'Lighting';
  private app!: App;
  private scene!: THREE.Scene;
  private renderer!: THREE.WebGPURenderer;
  private readonly sun = new THREE.DirectionalLight(0xfff2d8, 2.0);
  private readonly ambient = new THREE.HemisphereLight(0xbfd4ff, 0x4a4636, 0.5);
  private readonly profiles = new Map<Band, Profile>();
  private csm: CSMShadowNode | null = null;
  private readonly _tint = new THREE.Color();
  private cascadeCount = 0;
  private subscribed = false;
  private ready = false;
  /** Last tod the sun was REPOSITIONED at (CSM re-render gate) — see applyTimeOfDay. */
  private lastShadowTod = -1;

  // `assets` is kept for API compatibility with verify harnesses (g3-lighting,
  // g3-shadows, g5-tiers) that construct `new LightingModule(assets)`.
  // This module no longer calls `assets.loadEnvMap` — IBL comes from SkyModule.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.renderer = app.renderer.native as THREE.WebGPURenderer;
    // Tone mapping from the graphics settings (menu wave — operator + exposure
    // were hardcoded; ACES @0.85 stays the default via the preset fallbacks).
    this.applyTonemap();
    this.sun.target.position.set(0, 0, 0);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.02;
    this.scene.add(this.sun, this.sun.target, this.ambient);

    // CSM sun shadows (the WebGPU node CSM) — 4 cascades desktop / 2 mobile, fade between
    // them. Shadow cost is BOUNDED by the per-shadow autoUpdate gate (the WebGPU renderer
    // re-renders a LightShadow only when shadow.needsUpdate || shadow.autoUpdate), so a
    // static sun skips the cascade depth passes entirely; we flag an update on sun-move /
    // geometry change. (renderer.shadowMap on WebGPU only carries { enabled, type }.)
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.sunShadowGate().autoUpdate = false;
    this.buildCsm(); // sizes cascades / map size / far from the active quality tier (G5)

    bus.on('atlas:account-ready', () => { this.subscribe(); void this.boot(); });
    bus.on('atlas:structures-updated', () => this.requestShadowUpdate()); // casters changed
    bus.on('atlas:quality-changed', () => { this.buildCsm(); this.applyTonemap(); }); // G5: tier change rescales the shadow budget + tonemap
    for (const ev of ['atlas:environment-updated', 'atlas:world-entered'] as const) bus.on(ev, () => this.applyFromSky());

    // Continuous day/night drive (RenderSub/4, just ahead of the sky uniforms at 5).
    // The event hooks above only fire on world hops, so the 2 h clock used to leave
    // sun/ambient FROZEN at whatever band was active at boot — ambient never dimmed
    // at night. Now color + intensity track the override-aware sky clock every frame
    // (uniform-cheap); the sun's POSITION — which invalidates the CSM cascades —
    // steps behind a tod threshold inside applyTimeOfDay so shadows are not
    // re-rendered per frame.
    app.world.addSystem(makeSystem('AtlasDayNight', Phase.RenderSub, 4, () => {
      this.drainCsmDisposals(); // deferred CSM teardown (Dawn destroyed-texture guard)
      if (this.ready) this.applyTimeOfDay(currentTod());
    }, Sub.Update));
  }

  /**
   * (Re)build the CSM from the active quality tier — cascade count, per-cascade map size, and
   * far distance all scale with the tier (G5). Cheap to call on a tier change (rare); disposes the
   * previous node only after the new one is in place so a failed rebuild keeps the old shadows.
   * A coarse pointer (mobile) caps cascades at 2 regardless of tier.
   */
  /**
   * CSM nodes waiting for deferred disposal, with a countdown of rendered
   * frames. WHY: disposing a CSMShadowNode destroys its cascade shadow-map
   * textures IMMEDIATELY, but the pipelines compiled against them are only
   * re-keyed on the NEXT built frame (the castShadow flip changes the lights
   * hash). On WebGPU (Dawn) the in-flight frame then samples destroyed
   * textures — the "destroyed texture used in a submit" validation storm seen
   * when switching ultra → potato. Two frames of grace lets the rebuilt
   * pipelines land before the old textures die. Drained by AtlasDayNight
   * (RenderSub/4), which runs every frame.
   */
  private readonly pendingCsmDispose: Array<{ node: { dispose?: () => void }; frames: number }> = [];

  private deferCsmDispose(node: unknown): void {
    this.pendingCsmDispose.push({ node: node as { dispose?: () => void }, frames: 2 });
  }

  /** Called every frame from the AtlasDayNight system. */
  private drainCsmDisposals(): void {
    for (let i = this.pendingCsmDispose.length - 1; i >= 0; i--) {
      const p = this.pendingCsmDispose[i];
      if (--p.frames <= 0) {
        try { p.node.dispose?.(); } catch { /* already gone */ }
        this.pendingCsmDispose.splice(i, 1);
      }
    }
  }

  /** Operator + exposure from the active preset (live on quality-changed). */
  private applyTonemap(): void {
    const p = activePreset();
    const ops: Record<string, THREE.ToneMapping> = {
      aces: THREE.ACESFilmicToneMapping, agx: THREE.AgXToneMapping,
      neutral: THREE.NeutralToneMapping, reinhard: THREE.ReinhardToneMapping,
    };
    this.renderer.toneMapping = ops[p.tonemapOperator ?? 'aces'] ?? THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = p.tonemapExposure ?? 0.85; // 0.85: daytime read blown-out at 1.0
  }

  private buildCsm(): void {
    const preset = activePreset();
    // Potato / shadows-off: no CSM node at all — the sun stops casting and the
    // cascades are torn down. Every consumer of this.csm already null-guards
    // (hasCSM, requestShadowUpdate), so "no shadows" is just the null state.
    if (!preset.shadowsEnabled) {
      const prev = this.csm;
      this.csm = null;
      this.cascadeCount = 0;
      this.sun.castShadow = false;
      (this.sun.shadow as unknown as { shadowNode: CSMShadowNode | null }).shadowNode = null;
      if (prev) this.deferCsmDispose(prev);
      return;
    }
    this.sun.castShadow = true; // re-enable after a shadows-off tier (init default is true)
    this.sun.shadow.mapSize.set(preset.shadowMapSize, preset.shadowMapSize);
    // NORMAL BIAS SCALED TO THE TEXEL (2026-09-15). This was a fixed 0.02 m,
    // which only ever suited the tiers whose cascade-0 texel happens to be a
    // centimetre or two. normalBias offsets the shadow lookup ALONG the
    // surface normal, so the offset it needs is roughly one shadow-map texel
    // in world units — and that varies enormously across the ladder: ultra is
    // 4096 over ~400 m, minimal 256 over 40 m, a 24× difference in texel size.
    // Too small and grazing light acnes the ground; too large and small
    // objects peter-pan off their own contact shadow.
    //
    // Cascade 0 covers a fraction of maxFar under the 'practical' split, so
    // approximate its texel as maxFar/(4·mapSize) and offset by ~1.5 of them.
    // Clamped so no tier lands somewhere absurd.
    const texelM = preset.shadowMaxFar / (4 * Math.max(64, preset.shadowMapSize));
    this.sun.shadow.normalBias = Math.min(0.25, Math.max(0.015, texelM * 1.5));
    const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
    const cascades = coarse ? Math.min(2, preset.shadowCascades) : preset.shadowCascades;
    const prev = this.csm;
    try {
      const csm = new CSMShadowNode(this.sun, { cascades, maxFar: preset.shadowMaxFar, mode: 'practical', lightMargin: 100 });
      csm.fade = true;
      (this.sun.shadow as unknown as { shadowNode: CSMShadowNode }).shadowNode = csm;
      this.csm = csm;
      this.cascadeCount = cascades;
      if (prev) this.deferCsmDispose(prev); // old maps outlive the in-flight frame
      this.requestShadowUpdate();
    } catch (e) { console.warn('[g3] CSMShadowNode unavailable — single shadow map', e); }
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => void this.boot())
      // NOTE: 'SELECT * FROM render_asset' is still in the subscription so the
      // sky-band profiles (which reference envmap asset IDs) remain available for
      // the profile rows.  The envmap binaries are never loaded by this module.
      .subscribe(['SELECT * FROM sky_lighting_profile', 'SELECT * FROM render_asset', 'SELECT * FROM world', 'SELECT * FROM sky_state']);
  }

  /** Load the band profiles (sun color/intensity/ambient only), then apply the current time-of-day. */
  private async boot(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const rows = [...conn.db.skyLightingProfile.iter()];
    if (rows.length === 0) return; // profiles not replicated yet
    for (const r of rows) {
      const band = r.band as Band;
      this.profiles.set(band, {
        envmapAssetId: r.envmapAssetId, // retained in the Profile type for potential future use
        sunColorHex: r.sunColorHex,
        sunIntensity: r.sunIntensity,
        ambientIntensity: r.ambientIntensity,
      });
    }
    // ready as soon as profiles are loaded — IBL comes from SkyModule's PMREM bake
    this.ready = this.profiles.size > 0;
    this.applyFromSky();
  }

  /** True on the real WebGPU backend (cached once resolved — the backend object appears after async init). */
  private webGpuBackend: boolean | null = null;
  /** The 'atlas:no-day-trim' A/B escape hatch, re-read at most once a second —
   *  applyTimeOfDay runs every RenderSub frame and localStorage.getItem is a
   *  synchronous, non-JIT-able browser call (it was paid 60×/s). */
  private noDayTrimFlag = false;
  private noDayTrimAt = -Infinity;
  private noDayTrim(): boolean {
    const now = performance.now();
    if (now - this.noDayTrimAt > 1000) {
      this.noDayTrimAt = now;
      try { this.noDayTrimFlag = localStorage.getItem('atlas:no-day-trim') === '1'; } catch { this.noDayTrimFlag = false; }
    }
    return this.noDayTrimFlag;
  }

  private isWebGpuBackend(): boolean {
    if (this.webGpuBackend === null) {
      try {
        const backend = (this.app.renderer as unknown as { native?: { backend?: { constructor?: { name?: string } } } }).native?.backend;
        if (backend) this.webGpuBackend = backend.constructor?.name === 'WebGPUBackend';
      } catch { /* keep probing until the backend exists */ }
    }
    return this.webGpuBackend === true;
  }

  private applyFromSky(): void {
    // currentTod() resolves the CURRENT world's clock (or the debug override),
    // falling back to the Commons clock, then 0.5 — same source the sky renders by.
    if (this.ready) this.applyTimeOfDay(currentTod());
  }

  /** Drive the sun arc/color/intensity + ambient for a time-of-day (0..1). */
  applyTimeOfDay(tod: number): void {
    const { band, next, blend } = bandForTimeOfDay(tod);
    const a = this.profiles.get(band), b = this.profiles.get(next);
    if (!a || !b) return;
    // Color + intensity every call (uniform-cheap) — this is what darkens the night:
    // night band = sun 0.25 / ambient 0.15 vs day 2.8 / 0.5 (sky_lighting_profile).
    lerpColorHexInto(a.sunColorHex, b.sunColorHex, blend, this.sun.color);
    // lightingBoost (potato-only, 1 elsewhere): with bloom/clouds/fog-haze/surface
    // detail stripped, the tier loses the luminance those effects carry and reads
    // uniformly dark — compensate in the lights (free) rather than re-enabling
    // effects. Applied here, not in the profiles, so day/night banding is preserved.
    const boost = activePreset().lightingBoost;
    let si = THREE.MathUtils.lerp(a.sunIntensity, b.sunIntensity, blend);
    let ai = THREE.MathUtils.lerp(a.ambientIntensity, b.ambientIntensity, blend);
    // Bleached-noon trim (real-GPU agent probes, 2026-07-28): on the WebGPU
    // backend every NOON run described the ground as a pale washed-out sheet
    // while night renders read correct — the day band (sun 2.8 + ambient 0.5)
    // stacks on the Hillaire IBL environment and overshoots under ACES. Trim
    // scales with DAYNESS (full −12% at noon, →0 at night, so the tuned night
    // look is untouched). A/B escape hatch: localStorage 'atlas:no-day-trim'.
    if (this.isWebGpuBackend() && !this.noDayTrim()) {
      const trim = 1 - 0.12 * Math.min(1, si / 2.8);
      si *= trim;
      ai *= trim;
    }
    // The world's authored ENVIRONMENT (Edit Mode): brightness multipliers +
    // an optional sun tint, after the band blend so day/night still band.
    const env = worldEnv();
    this.sun.intensity = si * boost * env.sunMul;
    this.ambient.intensity = ai * boost * env.ambientMul;
    if (env.sunTintHex) this.sun.color.lerp(this._tint.set(env.sunTintHex), TINT_BLEND); // scratch — a Color was allocated every frame
    // Sun ARC only per ≥0.0005 tod (≈7 s of the 2 h cycle, sun ≈0.18°): moving the
    // sun invalidates every CSM cascade, so stepping it keeps shadows event-gated
    // instead of re-rendering all cascades every frame.
    if (Math.abs(tod - this.lastShadowTod) < 0.0005) return;
    this.lastShadowTod = tod;
    const p = sunPosition(tod);
    // Below the horizon the profile "sun" is really the dim blue night key
    // (moonlight stand-in) — mirror it above ground so it never up-lights
    // geometry from underneath. |y| is continuous across the horizon crossing.
    if (p.y < 0) p.y = -p.y;
    this.sun.position.copy(p);
    this.requestShadowUpdate(); // the sun moved → re-render the CSM shadows once
    // scene.environment and scene.background are owned by SkyModule (Hillaire bake).
    // This module no longer touches them.
  }

  private sunShadowGate(): { autoUpdate: boolean; needsUpdate: boolean } {
    return this.sun.shadow as unknown as { autoUpdate: boolean; needsUpdate: boolean };
  }

  /** Re-render the CSM shadows ONCE (sun moved / casters changed), keeping the cascades gated. */
  requestShadowUpdate(): void {
    this.sunShadowGate().needsUpdate = true;
    if (this.csm) for (const lw of this.csm.lights) { lw.shadow.autoUpdate = false; lw.shadow.needsUpdate = true; }
  }

  /**
   * Global IBL strength (environmentIntensity).  Retained for API compatibility
   * with the g3-lighting Playwright harness (`lighting.setEnvIntensity(v)`).
   * The harness tests envMapIntensity scaling on `scene.environmentIntensity`,
   * which the engine's PMREM cube respects regardless of which module sets it.
   */
  setEnvIntensity(v: number): void { this.scene.environmentIntensity = v; }

  /** Test/inspection hooks. */
  get sunLight(): THREE.DirectionalLight { return this.sun; }
  get ambientLight(): THREE.HemisphereLight { return this.ambient; }
  /**
   * `true` once the band profiles have loaded (sun color/intensity/ambient
   * data available). IBL itself is always live from SkyModule — it does not
   * gate on `isReady`.
   */
  get isReady(): boolean { return this.ready; }
  get shadowCascades(): number { return this.cascadeCount; }
  get shadowMapSize(): number { return this.sun.shadow.mapSize.x; }
  get shadowMaxFar(): number { return (this.csm as unknown as { maxFar?: number } | null)?.maxFar ?? 0; }
  get hasCSM(): boolean { return this.csm !== null; }

  dispose(): void {
    // SkyModule.dispose() handles the IBL bake lifecycle; the lights and the
    // live CSM (cascade shadow maps) are ours.
    this.drainCsmDisposals();
    try { this.csm?.dispose(); } catch { /* Dawn destroyed-texture guard */ }
    this.csm = null;
    this.sun.parent?.remove(this.sun);
    this.ambient.parent?.remove(this.ambient);
  }
}
